// Sentry frontend instrumentation. PERF-025: lazy-loaded via dynamic
// import so the ~55 KB gz of @sentry/react SDK never lands in the entry
// chunk. The SDK only fetches when (a) VITE_SENTRY_DSN is set AND (b)
// something actually needs to report — first call to captureException,
// setSentryUser, or clearSentryUser. No-ops when DSN is unset.
//
// Public API stays fire-and-forget: callers don't await. setUser/
// clearUser calls before Sentry has loaded buffer the intent in module-
// level state and apply once the SDK lands.
//
// PII scrub: Sentry's default capture grabs user emails, message bodies,
// and console output that can include prayer / chat content. The
// beforeSend hook strips event.user identifiers (we only send {id},
// never email), recursively redacts keys matching PII_KEY_REGEX from
// extra/contexts/breadcrumb data, and disables sendDefaultPii so
// headers/cookies/IP aren't auto-attached.

const DSN = import.meta.env.VITE_SENTRY_DSN

// Promise cache so the dynamic import only fires once. null when DSN
// missing; Promise<SentryModule> otherwise (resolves after init).
let sentryPromise = null

// Pending user state — captured by setSentryUser/clearSentryUser before
// Sentry has loaded. Applied in loadSentry() once the SDK is ready.
let pendingUserId = null
let pendingClear = false

const PII_KEY_REGEX = /email|prayer|message|chat|content|password|secret|token|display_?name/i

function scrubObject(obj) {
  if (!obj || typeof obj !== 'object') return
  for (const key of Object.keys(obj)) {
    if (PII_KEY_REGEX.test(key)) {
      obj[key] = '[scrubbed]'
    } else if (typeof obj[key] === 'object' && obj[key] !== null) {
      scrubObject(obj[key])
    }
  }
}

function scrubPII(event) {
  if (event.user) {
    delete event.user.email
    delete event.user.username
    delete event.user.ip_address
  }
  if (event.extra) scrubObject(event.extra)
  if (event.contexts) scrubObject(event.contexts)
  if (event.breadcrumbs) {
    for (const crumb of event.breadcrumbs) {
      if (crumb.data) scrubObject(crumb.data)
    }
  }
  return event
}

// Resolves to the Sentry module (post-init) or null when DSN is unset.
// Subsequent callers share the same promise.
function loadSentry() {
  if (!DSN) return null
  if (sentryPromise) return sentryPromise
  sentryPromise = import('@sentry/react')
    .then(Sentry => {
      try {
        Sentry.init({
          dsn: DSN,
          environment: import.meta.env.MODE,
          integrations: [Sentry.browserTracingIntegration()],
          // Capture every error event; rely on Sentry's dashboard spike
          // protection as the runaway-billing safety net. Performance
          // traces sampled at 10% so we still get a feel for slow paths
          // without exhausting the free tier.
          sampleRate: 1.0,
          tracesSampleRate: 0.1,
          sendDefaultPii: false,
          beforeSend: scrubPII,
        })
        // Apply any user state set before the SDK was loaded.
        if (pendingClear) {
          Sentry.setUser(null)
          pendingClear = false
        } else if (pendingUserId) {
          Sentry.setUser({ id: pendingUserId })
          pendingUserId = null
        }
        return Sentry
      } catch (err) {
        console.warn('[sentry] init failed:', err?.message || err)
        return null
      }
    })
    .catch(err => {
      console.warn('[sentry] dynamic import failed:', err?.message || err)
      return null
    })
  return sentryPromise
}

// Kept as a no-op shim — main.jsx no longer needs to call this, but the
// export stays so any future imports (or external scripts that referenced
// it) don't break. The disabled-DSN message lands once on the first
// captureException call instead of at startup.
export function initSentry() {
  if (!DSN && import.meta.env.DEV) {
    console.info('[sentry] disabled — set VITE_SENTRY_DSN to enable error reporting')
  }
}

export function setSentryUser(userId) {
  if (!userId) return
  pendingUserId = userId
  pendingClear = false
  const promise = loadSentry()
  if (!promise) return
  promise
    .then(Sentry => {
      if (Sentry && pendingUserId === userId) {
        Sentry.setUser({ id: userId })
        pendingUserId = null
      }
    })
    .catch(() => {})
}

export function clearSentryUser() {
  pendingClear = true
  pendingUserId = null
  const promise = loadSentry()
  if (!promise) return
  promise
    .then(Sentry => {
      if (Sentry && pendingClear) {
        Sentry.setUser(null)
        pendingClear = false
      }
    })
    .catch(() => {})
}

export function captureException(err, context) {
  const promise = loadSentry()
  if (!promise) return
  promise
    .then(Sentry => {
      if (!Sentry) return
      try {
        Sentry.captureException(err, context)
      } catch {
        // never let Sentry capture failures break the app
      }
    })
    .catch(() => {})
}
